// Copyright (c) 2026, Keyfactor, Inc
'use strict'

import '@fontsource/dm-sans/400.css'
import '@fontsource/dm-sans/500.css'
import '@fontsource/dm-sans/600.css'
import '@fontsource/dm-sans/700.css'
import '@fontsource/share-tech-mono'
import { useContext, useEffect, useState } from 'react'
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import styled, { useTheme } from 'styled-components'

import { getImage } from '@ipivot/themes-browser'

import ChangePasswordModal from 'js/components/modals/ChangePasswordModal'
import WaitModal from 'js/components/modals/WaitModal'

import { UiContext, SET_USER } from 'js/contexts/uiContext'

import { setupAxiosHooks } from 'js/helpers/axiosHelpers'

import { useDefaultGetQuery } from 'js/queries/defaultQueries'
import { useLoginMutation } from 'js/queries/loginQueries'

const Login = () => {
    const [ui, setUi] = useContext(UiContext) //eslint-disable-line no-unused-vars
    setupAxiosHooks(setUi)

    const [certBypassed, setCertBypassed] = useState(false)
    const [loginFailedErrorMessage, setLoginFailedErrorMessage] = useState(null)
    const [passwordExpired, setPasswordExpired] = useState(false)
    const [sendSamlRequest, setSendSamlRequest] = useState(false)
    const [sendOAuthRequest, setSendOAuthRequest] = useState(false)
    const [showWaitModal, setShowWaitModal] = useState(false)
    const [samlError, setSamlError] = useState(false)
    const [oauthError, setOAuthError] = useState(false)

    const { t } = useTranslation()
    const theme = useTheme()

    const { data: loginConfig, error: errorFetchingLoginConfig } = useDefaultGetQuery({ url: '/loginConfig' })
    const { mutate: loginMutate, isPending: isLoggingIn } = useLoginMutation()
    const { data: samlRequestData, error: samlRequestError } = useDefaultGetQuery({
        url: '/saml/request',
        useQueryParams: { enabled: loginConfig?.samlEnabled === true && sendSamlRequest },
        requestParams: { noAuthFailure: true },
    })
    const { data: oauthRequestData, error: oauthRequestError } = useDefaultGetQuery({
        url: '/oauth/request',
        useQueryParams: { enabled: loginConfig?.oauthEnabled === true && sendOAuthRequest },
        requestParams: { noAuthFailure: true },
    })

    useEffect(() => {
        setCertBypassed(!loginConfig?.certPresent)
    }, [loginConfig?.certPresent])

    useEffect(() => {
        let url = new URL(location.href)
        if (url.searchParams.get('samlError') === 'true') {
            setSamlError(true)
            url.searchParams.delete('samlError')
            history.pushState({}, '', url)
        }
        if (url.searchParams.get('oauthError') === 'true') {
            setOAuthError(true)
            url.searchParams.delete('oauthError')
            history.pushState({}, '', url)
        }
    }, [])

    useEffect(() => {
        if (samlRequestData?.response) window.location = samlRequestData.response

        setSendSamlRequest(false)
    }, [samlRequestData, samlRequestError])

    useEffect(() => {
        if (oauthRequestData?.response) window.location = oauthRequestData.response

        setSendOAuthRequest(false)
    }, [oauthRequestData, oauthRequestError])

    const {
        formState: { errors, isSubmitted },
        handleSubmit,
        register,
        setValue,
        getValues,
    } = useForm()

    const login = async data => {
        setLoginFailedErrorMessage(null)
        const { username, password } = data
        let loginPayload = { certBypass: certBypassed }

        if (loginConfig?.certPresent === true) {
            loginPayload.username = loginConfig.showUsername || certBypassed ? username : undefined
            loginPayload.password = loginConfig.showPassword || certBypassed ? password : undefined
        } else {
            loginPayload.username = username
            loginPayload.password = password
        }

        loginMutate(loginPayload, {
            onSuccess: query => {
                if (query.data.passwordExpired === true) {
                    setUi({ type: SET_USER, user: { uid: query.data.uid, username: loginPayload.username } })
                    document.getElementById('csrf').value = query.data.csrfToken
                    setPasswordExpired(true)
                } else {
                    let url = new URL(location.href)
                    if (url.searchParams.get('redirectUrl')?.length >= 0) {
                        window.location.href = url.searchParams.get('redirectUrl')
                    } else {
                        window.location.href = '/enc'
                    }
                }
            },
            onError: err => {
                let errorMessage = null

                if (err.code === 'ERR_NETWORK') {
                    setShowWaitModal(true)
                    return
                } else if (err.response?.status) {
                    switch (err.response?.status) {
                        case 401:
                            switch (err.response?.data) {
                                case 'Account expired':
                                    errorMessage = t('login.account_expired_label')
                                    break
                                case 'Account locked':
                                    errorMessage = t('login.account_locked_label')
                                    break
                                case 'Password expired':
                                    // This means the password expiration policy was set to 'reject' vs 'forceChange'
                                    errorMessage = t('login.password_expired_label')
                                    break
                                default:
                                    errorMessage = t('login.log_in_failed_label')
                                    break
                            }

                            break
                        case 403:
                            if (err.response?.data !== 'csrf') errorMessage = t('login.unexpected_login_error_label')
                            break
                        default:
                            errorMessage = t('login.unexpected_login_error_label')
                            break
                    }
                } else {
                    errorMessage = t('login.unexpected_login_error_label')
                }

                setLoginFailedErrorMessage(errorMessage)
            },
        })
    }

    return (
        <Container fluid>
            {loginConfig?.loginNotice?.length > 0 && (
                <LoginNoticeRow className="pt-3">
                    <Col sm={12}>
                        <h2 className="text-center pb-2">{loginConfig.loginNotice}</h2>
                    </Col>
                </LoginNoticeRow>
            )}
            <Row className="justify-content-center align-items-center" style={{ minHeight: '75vh' }}>
                <Col sm={12}>
                    <div className="p-5 pb-4 text-center">
                        <img src={getImage(theme.images.loginLogo)} alt={theme.images.logoImgAlt} width={250} />
                    </div>
                    <LoginCard className="highlight-card">
                        <Card.Body className="p-4 pb-5">
                            <h1 className="text-center mb-3">{t('login.log_in_title')}</h1>
                            {errorFetchingLoginConfig && <div className="text-danger mb-3">{t('login.failed_to_fetch_login_config_label')}</div>}
                            <Form onSubmit={handleSubmit(login)}>
                                {loginConfig === undefined || loginConfig?.showUsername === true || certBypassed ? (
                                    <Form.Group className="mb-3" noValidate validated={isSubmitted.toString()}>
                                        <Form.Label htmlFor="username">{t('login.username_placeholder')}</Form.Label>
                                        <Form.Control
                                            id="username"
                                            type="text"
                                            autoComplete="off"
                                            autoFocus={true}
                                            disabled={isLoggingIn}
                                            isInvalid={isSubmitted && errors.username}
                                            {...register('username', {
                                                required: 'Required',
                                            })}
                                        />
                                        {isSubmitted && errors.username && (
                                            <Form.Control.Feedback id="usernameValidation" type="invalid">
                                                {errors.username.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                ) : null}

                                {loginConfig === undefined || loginConfig?.showPassword === true || certBypassed ? (
                                    <Form.Group className="mb-3" noValidate validated={isSubmitted.toString()}>
                                        <Form.Label htmlFor="password">{t('login.password_placeholder')}</Form.Label>
                                        <Form.Control
                                            id="password"
                                            type="password"
                                            autoComplete="off"
                                            disabled={isLoggingIn}
                                            isInvalid={isSubmitted && errors.password}
                                            {...register('password', {
                                                required: 'Required',
                                            })}
                                        />
                                        {isSubmitted && errors.password && (
                                            <Form.Control.Feedback id="passwordValidation" type="invalid">
                                                {errors.password.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                ) : null}

                                {/* Logging in with a certificate */}
                                {loginConfig?.certPresent && (
                                    <a
                                        className="text-center pe-3 cursor-pointer"
                                        onClick={() => {
                                            setLoginFailedErrorMessage(null)
                                            setCertBypassed(!certBypassed)
                                        }}
                                    >
                                        {certBypassed ? t('login.login_using_certificate_label') : t('login.login_as_different_user_label')}
                                    </a>
                                )}

                                <div className="d-flex w-100">
                                    <Button className="w-100 mt-2" title={t('login.log_in_title')} disabled={isLoggingIn} type="submit">
                                        {isLoggingIn ? (
                                            <>
                                                <Spinner size="sm" className="me-1" />
                                                Authenticating
                                            </>
                                        ) : certBypassed ? (
                                            t('login.log_in_title')
                                        ) : (
                                            t('login.login_using_certificate_label')
                                        )}
                                    </Button>
                                </div>

                                {loginFailedErrorMessage && <div className="text-center text-danger mt-2">{loginFailedErrorMessage}</div>}
                            </Form>

                            {/* SSO controls and messaging */}
                            {loginConfig?.oauthEnabled || loginConfig?.samlEnabled ? (
                                <SsoSeparator className="mt-4 mb-4">
                                    <SsoSeparatorText>Or</SsoSeparatorText>
                                </SsoSeparator>
                            ) : null}
                            {loginConfig?.oauthEnabled && (
                                <div>
                                    <div className="text-center">
                                        <Button
                                            className="w-100"
                                            variant="outline-secondary"
                                            title={t('login.oauth_sign_on_title')}
                                            onClick={() => setSendOAuthRequest(true)}
                                        >
                                            {t('login.oauth_sign_on_title')}
                                        </Button>
                                    </div>
                                    {oauthRequestError && (
                                        <div className="text-center text-danger mt-2">{t('login.unexpected_login_error_label')}</div>
                                    )}
                                    {oauthError && (
                                        <div className="text-center text-danger mt-2">{t('login.unexpected_oauth_login_error_label')}</div>
                                    )}
                                </div>
                            )}
                            {loginConfig?.samlEnabled && (
                                <div className={loginConfig?.oauthEnabled ? 'mt-3' : ''}>
                                    <div className="text-center">
                                        <Button
                                            className="w-100"
                                            variant="outline-secondary"
                                            title={t('login.saml_sign_on_title')}
                                            onClick={() => setSendSamlRequest(true)}
                                        >
                                            {t('login.saml_sign_on_title')}
                                        </Button>
                                    </div>
                                    {samlRequestError && (
                                        <div className="text-center text-danger mt-2">{t('login.unexpected_login_error_label')}</div>
                                    )}
                                    {samlError && <div className="text-center text-danger mt-2">{t('login.unexpected_saml_login_error_label')}</div>}
                                </div>
                            )}
                        </Card.Body>
                    </LoginCard>
                </Col>
            </Row>

            {passwordExpired && (
                <ChangePasswordModal
                    isCancelable={false}
                    passwordExpired
                    onClose={newPassword => {
                        if (newPassword === undefined) return

                        //login with the new password to set correct capabilties
                        setValue('password', newPassword)
                        login(getValues())
                    }}
                />
            )}

            {showWaitModal && <WaitModal waitEvent="restartSystem" />}
        </Container>
    )
}

export default Login

//Style
const LoginCard = styled(Card)`
    width: 400px;
    margin: auto;
    padding-top: 75px;
    padding-bottom: 75px;
`

const LoginNoticeRow = styled(Row)`
    background: ${props => props.theme.colors.loginNotice.backgroundColor};
    color: ${props => props.theme.colors.loginNotice.color};
    box-shadow: 0 0 5px ${props => props.theme.colors.loginNotice.boxShadowColor};
`

const SsoSeparator = styled.div`
    display: flex;
    align-items: center;
    margin: 16px 0;
    white-space: nowrap;
    text-align: center;
    border-block-start: 0 rgba(5, 5, 5, 0.06);

    &:before {
        position: relative;
        width: 50%;
        border-block-start: 1px solid transparent;
        border-block-start-color: ${props => props.theme.colors.loginNotice.boxShadowColor};
        border-block-end: 0;
        transform: translateY(50%);
        content: '';
    }

    &:after {
        position: relative;
        width: 50%;
        border-block-start: 1px solid transparent;
        border-block-start-color: ${props => props.theme.colors.loginNotice.boxShadowColor};
        border-block-end: 0;
        transform: translateY(50%);
        content: '';
    }
`

const SsoSeparatorText = styled.span`
    display: inline-block;
    padding-block: 0;
    padding-inline: 1em;
`
